<template>
  <ManagerLayout>
    <div class="manager-onboarding">
      <h2 class="page-title">登録ステップ状況</h2>
      <p class="page-desc">
        保護者アカウントごとに、登録からどのステップまで完了しているかを表示します。
        「登録はあったのに提出が無い」ケースを見つけるための一覧です。
      </p>

      <section class="manager-section filters-section">
        <div class="card">
          <div class="filters">
            <label class="filter-item filter-search">
              <span class="filter-label">検索</span>
              <input
                v-model="searchQ"
                type="text"
                placeholder="メール・氏名で部分一致"
                class="form-input"
                @keyup.enter="loadUsers"
              />
            </label>
            <label class="filter-item filter-checkbox">
              <input v-model="onlyStuck" type="checkbox" @change="loadUsers" />
              <span>未提出のみ表示（初回提出がまだの人）</span>
            </label>
            <button type="button" class="btn btn-primary" :disabled="loading" @click="loadUsers">
              {{ loading ? '読み込み中...' : '検索' }}
            </button>
          </div>
        </div>
      </section>

      <section class="manager-section">
        <div class="card">
          <div class="toolbar">
            <span class="total-note">
              全 {{ users.length }} 件（登録日時が{{ sortOrder === 'desc' ? '新しい' : '古い' }}順）
            </span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="loading" @click="toggleSortOrder">
              {{ sortOrder === 'desc' ? '古い順に切替' : '新しい順に切替' }}
            </button>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="loading" @click="loadUsers">
              更新
            </button>
          </div>
          <div v-if="loading" class="loading-state">読み込み中...</div>
          <div v-else-if="!users.length" class="empty-state">該当するユーザーがいません</div>
          <div v-else class="table-wrap">
            <table class="data-table" aria-label="登録ステップ状況">
              <thead>
                <tr>
                  <th>メール</th>
                  <th>氏名</th>
                  <th>お子さま</th>
                  <th>登録日時（JST）</th>
                  <th class="center">メール認証</th>
                  <th class="center">情報入力</th>
                  <th class="center">LINE連携</th>
                  <th class="center">マイク設定</th>
                  <th class="center">チュートリアル</th>
                  <th class="center">初回提出</th>
                  <th>現在のステップ</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in users" :key="u.id" class="data-row" :class="{ stuck: !u.hasSubmission }">
                  <td>{{ u.email }}</td>
                  <td>{{ u.fullName || '—' }}</td>
                  <td>{{ u.learnerName || '—' }}</td>
                  <td>{{ formatDateTimeJst(u.createdAt) }}</td>
                  <td class="center">{{ u.emailConfirmed ? '✅' : '—' }}</td>
                  <td class="center">{{ u.profileCompleted ? '✅' : '—' }}</td>
                  <td class="center">{{ u.lineConnected ? '✅' : '—' }}</td>
                  <td class="center">{{ u.micSetupCompleted ? '✅' : '—' }}</td>
                  <td class="center">{{ u.tutorialCompleted ? '✅' : '—' }}</td>
                  <td class="center">{{ u.hasSubmission ? '✅' : '—' }}</td>
                  <td>
                    <span class="step-badge" :class="`step-${u.stepIndex}`">{{ u.stepLabel }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''

const searchQ = ref('')
const onlyStuck = ref(false)
const users = ref([])
const loading = ref(false)
const sortOrder = ref('desc') // 'desc' = 新しい順（標準）, 'asc' = 古い順

function toggleSortOrder () {
  sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc'
  loadUsers()
}

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function formatDateTimeJst (iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', dateStyle: 'short', timeStyle: 'short' })
}

const loadUsers = async () => {
  loading.value = true
  try {
    const token = await getToken()
    const params = new URLSearchParams()
    if (searchQ.value.trim()) params.set('q', searchQ.value.trim())
    if (onlyStuck.value) params.set('onlyStuck', 'true')
    params.set('sortOrder', sortOrder.value)
    const res = await fetch(`${apiBase}/api/manager/onboarding-progress?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      users.value = []
      return
    }
    users.value = data.users || []
  } catch (_) {
    users.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadUsers()
})
</script>

<style scoped>
.manager-onboarding {
  padding: 0;
}

.page-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  color: #1e293b;
}

.page-desc {
  margin: 0 0 1.5rem 0;
  color: #64748b;
  font-size: 0.9rem;
}

.manager-section {
  margin-bottom: 2rem;
}

.card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-checkbox {
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #334155;
}

.filter-label {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

.filter-search .form-input {
  min-width: 220px;
}

.form-input {
  padding: 0.4rem 0.6rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.9rem;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.total-note {
  font-size: 0.9rem;
  color: #64748b;
}

.btn {
  padding: 0.4rem 0.9rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  border: 1px solid transparent;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #e2e8f0;
  color: #334155;
  border-color: #cbd5e1;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.data-table th,
.data-table td {
  padding: 0.5rem 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.data-table th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
}

.data-table th.center,
.data-table td.center {
  text-align: center;
}

.data-row.stuck {
  background: #fffbeb;
}

.step-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.82rem;
  white-space: nowrap;
}

.step-1 {
  background: #fee2e2;
  color: #991b1b;
}

.step-2 {
  background: #fef3c7;
  color: #92400e;
}

.step-3 {
  background: #e0e7ff;
  color: #3730a3;
}

.step-4 {
  background: #dbeafe;
  color: #1e40af;
}

.step-5 {
  background: #ede9fe;
  color: #5b21b6;
}

.step-6 {
  background: #fae8ff;
  color: #86198f;
}

.step-7 {
  background: #dcfce7;
  color: #166534;
}
</style>
